import { ChatMessageHistoryDataAction } from '../../Actions/ChatHistory';
import { RecentChatUpdateAction } from '../../Actions/RecentChatActions';
import { changeTimeFormat } from '../../Helpers/Chat/RecentChat';
import logger from '../../Helpers/logger';
import { getUserIdFromJid } from '../../Helpers/Utility';
import { pollLabelsReceived, pollsMarkedStale } from '../../slice/pollSlice';
import { buildCreatedLabel } from './pollLabels';
import { isAtFirstPosition, POLL_EVENT, POLL_MESSAGE_TYPE, toMillis } from './pollRules';

// Checked against events, not the stored poll: the actor's REST answer arrives first.
const appliedVersions = new Map();

const eventMeta = event => ({
    pollId: event.pollId,
    pollEvent: event.event,
    pollActor: event.actor,
    pollReason: event.reason,
});

/**
 * Forgets the event versions applied in this session, e.g. on logout.
 */
export const resetPollEvents = () => {
    appliedVersions.clear();
};

/**
 * Applies a <poll_event>: moves the poll message to the bottom when the server moved it, adds
 * the activity label, and has the poll reloaded. Runs synchronously so events apply in order.
 *
 * @param {object} event Event from the SDK pollEventListener.
 */
export const applyPollEvent = event => (dispatch, getState) => {
    const { pollId, messageId, groupJid } = event || {};
    if (!pollId || !groupJid) return;
    const chatId = getUserIdFromJid(groupJid);
    const version = Number(event.version) || 0;
    if (version <= (appliedVersions.get(pollId) || 0)) return;
    appliedVersions.set(pollId, version);
    const state = getState();

    const message = state.chatConversationHistory?.data?.[chatId]?.messages?.[messageId];
    if (!(message && event.bump)) {
        dispatch(updateRecentForEvent(state, chatId, event));
    }
    // Not in the local thread, e.g. after the viewer cleared the chat: nothing to move.
    if (message) {
        const time = event.timestamp || Date.now();
        const labels = [];
        if (event.bump) {
            const poll = state.poll.byId[pollId];
            if (!hasCreatedLabel(state, chatId, pollId) && isAtFirstPosition(message, poll)) {
                labels.push(createdLabel(message, poll));
            }
            dispatch(moveToBottom(message, groupJid, time, event));
        }
        if (event.activityId) {
            labels.push({
                activityId: event.activityId,
                pollId,
                messageId,
                event: event.event,
                actor: event.actor,
                reason: event.reason,
                time,
                anchorTime: event.bump ? time : toMillis(message.timestamp),
            });
        }
        if (labels.length) dispatch(pollLabelsReceived({ chatId, labels }));
    }
    dispatch(pollsMarkedStale([pollId]));
};

/**
 * Keeps the activity labels a page of chat history brought.
 *
 * @param {object} payload SDK pollActivitiesListener payload: groupJid and activities,
 * each with the anchorTime of the message it sits above.
 */
export const receivePollActivities = payload => dispatch => {
    const { groupJid, activities = [] } = payload || {};
    const chatId = getUserIdFromJid(groupJid || '');
    const labels = activities
        .filter(activity => activity.anchorTime)
        .map(activity => ({ ...activity, anchorTime: toMillis(activity.anchorTime) }));
    if (!chatId || !labels.length) return;
    dispatch(pollLabelsReceived({ chatId, labels }));
};

const hasCreatedLabel = (state, chatId, pollId) =>
    Object.values(state.poll.labelsByChat[chatId] || {}).some(
        label => label.pollId === pollId && label.event === POLL_EVENT.CREATED,
    );

// The first move stores "created" at the old position, but no event carries it.
const createdLabel = (message, poll) => ({
    ...buildCreatedLabel(message, poll),
    time: toMillis(poll?.createdAt) || toMillis(message.timestamp),
    anchorTime: toMillis(message.timestamp),
});

const RECENT_EVENTS = [POLL_EVENT.VOTED, POLL_EVENT.VOTE_CHANGED, POLL_EVENT.CLOSED, POLL_EVENT.RESHARED];

/**
 * Points the chat list row at the poll activity when the thread is not loaded or the poll did
 * not move. The row changes only when the poll is, or has just become, the last message.
 *
 * @param {object} state Redux state before the event.
 * @param {string} chatId Group id.
 * @param {object} event Poll event.
 */
const updateRecentForEvent = (state, chatId, event) => dispatch => {
    if (!RECENT_EVENTS.includes(event.event)) return;
    const row = (state.recentChatData?.data || []).find(item => item.fromUserId === chatId);
    const isPollLast = String(row?.msgId) === String(event.messageId);
    if (!row || (!isPollLast && !event.bump)) return;

    const poll = state.poll.byId[event.pollId];
    // Without the poll's own body the row would keep the previous message's body.
    const pollBody = !isPollLast &&
        poll && {
            msgBody: {
                message: '',
                message_type: POLL_MESSAGE_TYPE,
                poll: { pollId: poll.pollId, question: poll.question, createdBy: poll.createdBy },
            },
        };
    const time = event.bump && event.timestamp;
    dispatch(
        RecentChatUpdateAction({
            msgId: event.messageId,
            msgType: POLL_MESSAGE_TYPE,
            metaData: eventMeta(event),
            ...pollBody,
            ...(time && { timestamp: time, createdAt: changeTimeFormat(time) }),
            filterBy: chatId,
        }),
    );
};

const moveToBottom = (message, groupJid, time, event) => dispatch => {
    const moved = {
        ...message,
        timestamp: time,
        createdAt: changeTimeFormat(time),
        pollMoved: true,
        ...(event.rowId && { rowId: event.rowId }),
    };
    dispatch(ChatMessageHistoryDataAction({ data: [moved], groupJid, realTimeUpdate: true }));
    // A moved poll is not a new message: unread stays, only preview and time change.
    dispatch(
        RecentChatUpdateAction({
            ...moved,
            msgType: POLL_MESSAGE_TYPE,
            metaData: eventMeta(event),
            filterBy: getUserIdFromJid(groupJid),
        }),
    );
    logger.debug('Poll moved', { pollId: event.pollId, version: event.version });
};
